// Featurebase (feedback + support in-app), issues #724 / #736 / #738.
//
// Pack dédié, chargé par les layouts `application` et `saas` (visiteur
// anonyme ou membre connecté) quand `featurebase_enabled?` est vrai côté
// Rails. Le pack partagé `application` est volontairement laissé de côté :
// il est inclus par les layouts embed (`widget`, `widget_simulator`) et
// `admin`, où Featurebase ne doit pas démarrer.
//
// Le SDK injecte lui-même son runtime (https://do.featurebase.app/js/sdk.js) :
// aucune balise <script> à poser à la main. Les modules effectivement démarrés
// (messenger, changelog, sondages…) sont lus côté serveur depuis le dashboard.
//
// Identité (#738) : le pack ne s'évalue qu'une fois par chargement de page.
// Turbolinks remplace ensuite le `<body>`, donc le JWT (`data-featurebase-jwt`).
// On ne reboote pas à chaque `turbolinks:load` (le messenger redémarrerait) :
// seulement quand l'identité change. Le logout Devise est un POST natif, hors
// Turbolinks : `shutdown()` est appelé avant l'envoi du formulaire, sinon le
// runtime conserverait la session messenger du membre.
import Featurebase, { clearConfig, shutdown } from "featurebase-js";

// Le pack est servi dans le <head> (cf. layouts) pour n'être évalué qu'une
// fois par chargement de page : posé en fin de <body>, Turbolinks le
// réexécuterait à chaque navigation et le messenger redémarrerait.
// Contrepartie : `document.body` n'existe pas encore à l'évaluation du module.
//
// Turbolinks remplace ensuite `document.body` à chaque visite membre. Le
// lanceur vit dans ce body : le nœud disparaît, le runtime reste en mémoire
// (un second `Featurebase()` ne fait qu'un identify). Le SDK s'en aperçoit
// et remonte le messenger, mais ce redémarrage est asynchrone et plafonné
// (au-delà le lanceur reste absent). On force donc le montage shell v2 sur
// `<html>` — que Turbolinks ne remplace pas — le temps du boot.
const MOUNT_HOST_CLASS = "featurebase-mount-host";
const MESSENGER_ROOT_ID = "fb-messenger-root";
// Le module Support peut être coupé dans le dashboard : aucun root n'est
// alors injecté, et la classe doit quand même partir.
const MOUNT_HOST_TIMEOUT_MS = 10000;

// `shutdown()` attend 250 ms avant de démonter (le SDK coalesce les
// démontages/remontages React). Un `Featurebase()` dans cet intervalle
// annule le shutdown et conserve l'identité du membre. On ne reboote, et
// on ne laisse partir le POST de logout, qu'une fois ce délai écoulé.
const SDK_SHUTDOWN_GRACE_MS = 250;
const AFTER_SHUTDOWN_MS = SDK_SHUTDOWN_GRACE_MS + 50;

// `null` : le SDK n'a encore rien reçu. `""` : boot anonyme. Toute autre
// valeur : JWT du membre appliqué au dernier `Featurebase()`.
let appliedJwt = null;
let identityGeneration = 0;

function releaseMountHost(observer) {
  if (observer) observer.disconnect();
  if (document.body) document.body.classList.remove(MOUNT_HOST_CLASS);
}

function holdMountHostUntilRoot() {
  document.body.classList.add(MOUNT_HOST_CLASS);

  const observer = new MutationObserver(() => {
    const root = document.getElementById(MESSENGER_ROOT_ID);

    if (root && root.parentNode === document.documentElement) {
      releaseMountHost(observer);
    }
  });

  observer.observe(document.documentElement, { childList: true });
  window.setTimeout(() => releaseMountHost(observer), MOUNT_HOST_TIMEOUT_MS);
}

function readAppId() {
  if (!document.body) return "";

  return document.body.dataset.featurebaseAppId || "";
}

function readJwt() {
  if (!document.body) return "";

  return document.body.dataset.featurebaseJwt || "";
}

// `boot`     : premier passage.
// `login`    : anonyme → membre. `Featurebase()` ré-identifie sans démontage.
// `logout`   : membre → anonyme. `shutdown()` puis reboot anonyme.
// `switch`   : un JWT remplace un autre (autre compte sur le même navigateur).
// `same`     : rien à faire — un `turbolinks:load` ordinaire.
function identityChange(previousJwt, nextJwt) {
  if (previousJwt === null) return "boot";
  if (nextJwt === previousJwt) return "same";
  if (!nextJwt) return "logout";
  if (previousJwt) return "switch";

  return "login";
}

function bootFeaturebase(appId, jwt) {
  if (!document.getElementById(MESSENGER_ROOT_ID)) {
    holdMountHostUntilRoot();
  }

  const settings = { appId: appId };

  if (jwt) settings.featurebaseJwt = jwt;

  Featurebase(settings);
}

function syncIdentity() {
  const appId = readAppId();
  const jwt = readJwt();

  if (!appId) {
    if (appliedJwt !== null) teardown();
    return;
  }

  const change = identityChange(appliedJwt, jwt);

  if (change === "same") return;

  if (change === "boot" || change === "login") {
    identityGeneration += 1;
    bootFeaturebase(appId, jwt);
    appliedJwt = jwt;
    return;
  }

  identityGeneration += 1;
  const generation = identityGeneration;

  shutdown();
  clearConfig();
  appliedJwt = jwt;

  window.setTimeout(() => {
    if (generation !== identityGeneration) return;
    if (readAppId() !== appId || readJwt() !== jwt) return;

    bootFeaturebase(appId, jwt);
  }, AFTER_SHUTDOWN_MS);
}

function teardown() {
  identityGeneration += 1;
  shutdown();
  clearConfig();
  appliedJwt = null;
}

// Logout « dur » : `button_to method: :delete` poste le formulaire en
// navigation complète, Turbolinks n'est pas dans le chemin. Il faut
// démonter le messenger avant que la requête ne détruise la session,
// et laisser passer le délai de grâce du SDK — sinon le timer est tué
// par le déchargement de la page et l'identité reste en mémoire.
function isLogoutForm(form) {
  if (!form || form.nodeName !== "FORM") return false;

  const action = form.getAttribute("action") || "";

  if (!/\/sign_out(?:$|\?)/.test(action)) return false;

  const override = form.querySelector('input[name="_method"]');
  const method = ((override && override.value) || form.getAttribute("method") || "").toLowerCase();

  return method === "delete";
}

document.addEventListener(
  "submit",
  (event) => {
    const form = event.target;

    if (!isLogoutForm(form)) return;

    event.preventDefault();
    event.stopPropagation();

    if (form.dataset.featurebaseIdentityCleared === "true") return;

    form.dataset.featurebaseIdentityCleared = "true";
    teardown();
    window.setTimeout(() => form.submit(), AFTER_SHUTDOWN_MS);
  },
  true
);

document.addEventListener("turbolinks:load", syncIdentity);

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", syncIdentity, { once: true });
} else {
  syncIdentity();
}
